import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import '../../fonts/fonts.css'
import shoppingBagIcon from '../../images/icon-shopping-bag.png'
import clockIcon from '../../images/icon-clock.png'
import arrowIcon from '../../images/icon-arrow.svg'
import cardBg from '../../images/card-formation-bg.png'
import {
  PageHeader, PageIconBox, PageIconImg, PageTitleText, PageSubtitleText,
  FilterArea, FilterTopRow, FilterTitleGroup, FilterTitle, FilterCount,
  ContentArea,
  Grid, CardWrapper, Card, CardImage, CardImageEl, CardImageBadge, CardIconImg,
  CardBody, CardMeta, CategoryTag, Duration, CardTitle, CardDescription,
  CardFooter, PriceLabel, PriceValue, GroupPrices, ViewButton,
  EmptyState, EmptyIcon, EmptyText
} from './styledComponents'
import { describeGroupPrices } from '../ProductsLoader/saga'

const CATEGORY_COLORS = {
  Eval:        '#6462FC',
  Conduite:    '#3494FA',
  Automatique: '#31D0BE',
  Eco:         '#00C52B',
  Recyclage:   '#7C4DFF',
  Code:        '#3494FA',
  Hardware:    '#00C8F8'
}

const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €'

const toArray = (value) => {
  if (Array.isArray(value)) return value
  if (value && typeof value.toJS === 'function') {
    const js = value.toJS()
    return Array.isArray(js) ? js : []
  }
  return []
}

const ProductImg = ({ imageUrls, fallback }) => {
  const [failed, setFailed] = React.useState(false)
  const src = (!failed && imageUrls && imageUrls[0]) || fallback
  return <CardImageEl src={src} alt='' onError={() => setFailed(true)} />
}

const FormationCard = ({ formation, onClick }) => {
  const color = formation.color || CATEGORY_COLORS[formation.category] || '#3494FA'
  return (
    <CardWrapper onClick={() => onClick(formation)}>
      <Card>
        <CardImage>
          <ProductImg imageUrls={formation.imageUrls} fallback={cardBg} />
          {formation.iconUrl && (
            <CardIconImg src={formation.iconUrl} alt='' />
          )}
          {formation.badge && (
            <CardImageBadge>{formation.badge}</CardImageBadge>
          )}
        </CardImage>
        <CardBody>
          <CardMeta>
            <CategoryTag>{formation.category}</CategoryTag>
            <Duration>
              <img src={clockIcon} alt='' style={{ width: 11, height: 11, objectFit: 'contain' }} />
              {formation.duration.toUpperCase()}
            </Duration>
          </CardMeta>
          <CardTitle color={color}>{formation.title}</CardTitle>
          <CardDescription>{formation.shortDescription}</CardDescription>
          <CardFooter>
            <div>
              <PriceLabel>À partir de</PriceLabel>
              <PriceValue>{formation.price != null ? fmt(formation.price) : '—'}</PriceValue>
              {formation.groupPrices && formation.groupPrices.length > 0 && (
                <GroupPrices>{describeGroupPrices(formation.groupPrices)}</GroupPrices>
              )}
            </div>
            <ViewButton>
              Voir le détail
              <img src={arrowIcon} alt='' style={{ width: 20, height: 10, objectFit: 'contain' }} />
            </ViewButton>
          </CardFooter>
        </CardBody>
      </Card>
    </CardWrapper>
  )
}

const Store = () => {
  const history  = useHistory()
  const productsState = useSelector(state => state.getIn(['global', 'products']))
  const isAdminRaw = useSelector(state => state.getIn(['global', 'isAdmin']))
  const agencyProductsState = useSelector(state => state.getIn(['global', 'agencyProducts']))
  const loading  = useSelector(state => state.getIn(['global', 'productsLoading']))
  const errorState = useSelector(state => state.getIn(['global', 'productsError']))
  const products = useMemo(
    () => toArray(productsState),
    [productsState]
  )
  const agencyProducts = useMemo(
    () => toArray(agencyProductsState),
    [agencyProductsState]
  )
  const error = errorState || ''
  const isAdmin = isAdminRaw === true || isAdminRaw === 'true' || isAdminRaw === 1 || isAdminRaw === '1'

  const visibleProducts = useMemo(() => {
    if (isAdmin) return products
    const allowed = new Set((agencyProducts || []).map(String))
    return products.filter(p => allowed.has(String(p.id ?? p._id ?? '')))
  }, [isAdmin, products, agencyProducts])

  const handleCardClick = (f) => {
    const idCandidate = f?.id
    const hasValidId = idCandidate !== undefined && idCandidate !== null && idCandidate !== '' && idCandidate !== 'undefined' && idCandidate !== 'null'
    const productId = hasValidId ? idCandidate : f?._id
    console.log('[Store] product click', {
      product: f,
      idCandidate,
      hasValidId,
      resolvedProductId: productId,
      targetPath: productId ? `/store/${productId}` : null,
      isAdminRaw,
      isAdmin,
      agencyProductsCount: Array.isArray(agencyProducts) ? agencyProducts.length : 0,
    })
    if (!productId) return
    history.push(`/store/${productId}`, { formation: f })
  }

  // Rechargement en arrière-plan (renouvellement des URLs) : on garde la liste affichée
  if (loading && products.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: 'Poppins', fontSize: '1rem', color: '#BABABA' }}>
        Chargement des produits…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '0.75rem', fontFamily: 'Poppins' }}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        <span style={{ fontSize: '1rem', color: '#FF4D4D' }}>Impossible de charger les produits</span>
        <span style={{ fontSize: '0.875rem', color: '#BABABA' }}>{error}</span>
      </div>
    )
  }

  return (
    <>
      <PageHeader>
        <PageIconBox>
          <PageIconImg src={shoppingBagIcon} alt='' />
        </PageIconBox>
        <div>
          <PageTitleText>Magasin en ligne</PageTitleText>
          <PageSubtitleText>Achetez vos formations et équipements directement depuis votre espace professionnel</PageSubtitleText>
        </div>
      </PageHeader>

      <FilterArea>
        <FilterTopRow>
          <FilterTitleGroup>
            <FilterTitle>Nos produits</FilterTitle>
            <FilterCount>{visibleProducts.length} disponible{visibleProducts.length > 1 ? 's' : ''}</FilterCount>
          </FilterTitleGroup>
        </FilterTopRow>
      </FilterArea>

      <ContentArea>
        {visibleProducts.length === 0 && (
          <EmptyState>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyText>Aucun produit disponible pour le moment.</EmptyText>
          </EmptyState>
        )}
        <Grid>
          {visibleProducts.map(f => (
            <FormationCard key={f.id || f._id} formation={f} onClick={handleCardClick} />
          ))}
        </Grid>
      </ContentArea>
    </>
  )
}

export default Store
